import React from 'react';

import * as Yup from 'yup';
import * as Sentry from '@sentry/react';

import { Form, Formik, FormikHelpers } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Spinner,
  useToastContext,
} from '@resideo/blueprint-react';
import { InputField } from '@resideo/blueprint-formik';

import ModalHeader from 'components/common/ModalHeader';
import Typography from 'components/common/Typography';

import { useRisClient } from 'hooks/useRisClient';

import {
  RisError,
  StepComponentProps,
  Steps,
  PartnerCloudUserAuthPayload,
  PartnerCloudUserAuthRequest,
} from 'types/types';

const { Text } = Typography;

const isValidationFailedError = (error: RisError) =>
  error.status === 400 &&
  error.body?.errors?.some(e => e.code === 'VALIDATION_FAILED');

const SignInStep = ({
  account,
  closeModal,
  setUserSession,
  setStep,
}: StepComponentProps) => {
  const { client } = useRisClient();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const initialValues = {
    userId: '',
    password: '',
  };

  const SignInSchema = Yup.object().shape({
    userId: Yup.string().required(t('common.validation.required')),
    password: Yup.string().required(t('common.validation.required')),
  });

  const signIn = useMutation({
    mutationKey: ['dataStream', 'signIn'],
    mutationFn: async ({
      partnerCloudId,
      requestBody,
    }: PartnerCloudUserAuthRequest) => {
      const { data } =
        await client.partnerClouds.myUsersAuthenticatePartnerCloudUser(
          partnerCloudId,
          requestBody
        );

      if (!data?.accessToken || !data?.externalUserId) {
        Sentry.captureMessage(
          `[Data Streaming] Partner account authentication failed: incomplete response from service provider`,
          'error'
        );

        throw new Error(
          '[Data Streaming] Partner account authentication failed: incomplete response from service provider'
        );
      }

      return {
        accessToken: data.accessToken,
        externalUserId: data.externalUserId,
        encryptedDeviceData: data.encryptedDeviceData || '',
      };
    },

    onSuccess: data => {
      setUserSession(data);
      setStep(Steps.TERMS_ACCEPTANCE);
    },

    onError: (error: RisError, variables) => {
      if (!isValidationFailedError(error)) {
        addToast({
          toastType: 'Error',
          message: t(
            'privacy.dataStream.accountSignIn.credentials.errors.unknown'
          ),
        });

        Sentry.captureEvent({
          message: '[Data Streaming] Error signing in to partner account',
          level: 'error',
          extra: { variables },
        });
      }
    },

    retry: false,
  });

  const handleSubmit = (
    requestBody: PartnerCloudUserAuthPayload,
    formikHelpers: FormikHelpers<typeof initialValues>
  ) => {
    const payload: PartnerCloudUserAuthRequest = {
      partnerCloudId: account.partnerCloudId,
      requestBody,
    };

    signIn.mutate(payload, {
      onError: error => {
        if (isValidationFailedError(error)) {
          formikHelpers.setFieldTouched('userId', true, false);
          formikHelpers.setFieldTouched('password', true, false);
          formikHelpers.setFieldError(
            'password',
            t(
              'privacy.dataStream.accountSignIn.credentials.errors.invalidCredentials'
            )
          );
        }
      },
    });
  };

  return (
    <>
      <ModalHeader
        title={t('privacy.dataStream.accountSignIn.credentials.title', {
          accountName: account.partnerCloudName,
        })}
        closeModal={closeModal}
      />

      <Text marginBottom='medium'>
        {t('privacy.dataStream.accountSignIn.credentials.description')}
      </Text>

      <p>{t('common.requiredFields')}</p>

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={SignInSchema}>
        {({ dirty, isValid }) => (
          <Form>
            <InputField
              data-test='data-stream-sign-in-credentials-username'
              type='text'
              name='userId'
              label={t('privacy.dataStream.accountSignIn.credentials.username')}
              version='v2'
              required
            />

            <InputField
              data-test='data-stream-sign-in-credentials-password'
              type='password'
              name='password'
              label={t('privacy.dataStream.accountSignIn.credentials.password')}
              version='v2'
              required
            />

            <Box
              data-test='data-stream-sign-in-credentials-actions'
              paddingTop='large'>
              <Button type='submit' disabled={!dirty || !isValid} width={1}>
                {signIn.isPending ? (
                  <Spinner aria-label={t('common.signingIn')} color='white' />
                ) : (
                  t('common.signIn')
                )}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SignInStep;
